import { memo } from 'react';

import { merge } from '@/components';
import { BasicTooltip, Flex, Heading, Text } from '@/components/core';
import { ArrowIcon } from '@/components/icons';
import { DivProps } from '@/utils';

import { useChangeVariant } from './useChangeVariant';

export type MetricCardProps = Omit<DivProps, 'children'> & {
  heading: string;
  value: number;
  change: number;
  datapoints?: number;
  datapointsLabel: string;
  lowerIsBetter?: boolean;
  tooltip?: string;
  isPercentage?: boolean;
  isSelected?: boolean;
  isEmpty?: boolean;
};

const IMetricCard = ({
  heading,
  value,
  change,
  datapoints,
  datapointsLabel,
  lowerIsBetter,
  tooltip,
  isPercentage,
  isSelected,
  isEmpty,
  className,
  ...rest
}: MetricCardProps) => {
  const changeVariant = useChangeVariant(change, lowerIsBetter);
  const valueText = isPercentage ? `${(value * 100).toFixed(1)}%` : value.toString();
  const changeText = isPercentage ? `${(Math.abs(change) * 100).toFixed(1)}%` : Math.abs(change).toString();

  return (
    <div
      className={merge(
        'group/metric-card hover:bg-gray-25 flex h-fit cursor-pointer flex-col gap-4 p-4',
        isSelected ? 'border-b-primary-500 border-b-2' : 'border-b border-gray-200',
        className
      )}
      {...rest}
    >
      <div className="flex items-center gap-2">
        <Heading className="font-medium text-gray-600" size="md">
          {heading}
        </Heading>
        {tooltip ? (
          <div className="hidden group-hover/metric-card:block">
            <BasicTooltip placement="top">
              <Text>{tooltip}</Text>
            </BasicTooltip>
          </div>
        ) : null}
      </div>
      <Flex className="items-center gap-2" variant="plain">
        <Text className="font-semibold text-gray-900" size="xl">
          {isEmpty ? '—' : valueText}
        </Text>
        {!isEmpty && Math.abs(change) >= 0.01 ? (
          <Flex>
            <ArrowIcon variant={changeVariant} rotation={change > 0 ? 'up' : 'down'} />
            <Text variant={changeVariant} className="font-medium">
              {changeText}
            </Text>
          </Flex>
        ) : null}
      </Flex>
      <Flex className="items-center gap-2" variant="plain">
        <Text className="text-xs text-gray-500">
          {!isEmpty && datapoints != null ? `${datapoints} ${datapointsLabel}` : 'No data yet'}
        </Text>
      </Flex>
    </div>
  );
};

export const MetricCard = memo(IMetricCard) as typeof IMetricCard;
